import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import Brightness5Icon from "@mui/icons-material/Brightness5";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import {
  Alert,
  Button,
  Checkbox,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import TextField from '../SanitizedTextField/SanitizedTextField';
import PropTypes from "prop-types";
import {
  buildDesignationLookupMap,
  isDesignationLabel,
  lookupDesignationName,
} from "../../utils/designationDisplay";
import { isShiftSelected } from "../../utils/workforceShiftIds";

const fieldLabelSx = {
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "#475569",
  mb: 0.75,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const readOnlySelectSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#f1f5f9",
    borderRadius: "8px",
  },
  "& .MuiSelect-select.Mui-disabled": {
    WebkitTextFillColor: "#334155",
    color: "#334155",
  },
};

const readOnlyInputSx = {
  "& .MuiInputBase-root": {
    bgcolor: "#f1f5f9",
    borderRadius: "8px",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#334155",
    color: "#334155",
  },
};

const editableInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#fff",
    "& fieldset": {
      borderColor: "#cbd5e1",
    },
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#006ad1",
    },
  },
};

function ShiftIcon({ shiftName }) {
  const lower = shiftName.toLowerCase();
  if (lower.includes("mor")) {
    return <WbSunnyIcon fontSize="small" />;
  }
  if (lower.includes("eve")) {
    return <Brightness5Icon fontSize="small" />;
  }
  return <BedtimeIcon fontSize="small" />;
}

export default function EditLinemanDialog({
  open,
  onClose,
  formik,
  apiError,
  shifts,
  selectedShiftIds,
  onShiftToggle,
  designations,
  subdivisionDesignations = [],
  designationLabel = "",
}) {
  const showError = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  const designationMap = buildDesignationLookupMap(
    designations,
    subdivisionDesignations,
  );

  const designationName =
    (isDesignationLabel(designationLabel, formik.values.iv_designation)
      ? designationLabel
      : "") ||
    lookupDesignationName(formik.values.iv_designation, designationMap) ||
    "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="lineman-alert-popups lineman-form-dialog lineman-form-dialog--reference"
      PaperProps={{
        className: "lineman-form-dialog__paper",
      }}
    >
      <DialogTitle
        component="div"
        className="dialog-title lineman-form-dialog__title"
      >
        <Typography component="h2" className="lineman-form-dialog__heading">
          Edit Lineman
        </Typography>
        <IconButton
          id="clearPopup"
          aria-label="Close"
          onClick={onClose}
          size="small"
          className="lineman-form-dialog__close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="lineman-form-dialog__content">
        {apiError ? (
          <Alert severity="error" className="lineman-form-dialog__alert">
            {apiError}
          </Alert>
        ) : null}

        <Grid container spacing={2.5} className="lineman-form-dialog__grid">
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Name</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_name"
              value={formik.values.iv_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(showError("iv_name"))}
              helperText={showError("iv_name")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Designation</Typography>
            <FormControl
              fullWidth
              size="small"
              disabled
              className="lineman-form-dialog__designation"
              sx={readOnlySelectSx}
            >
              <Select value={designationName} displayEmpty>
                <MenuItem value={designationName}>{designationName}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} className="lineman-form-dialog__shift-col">
            <Typography sx={fieldLabelSx}>Shift</Typography>
            <FormGroup className="lineman-form-dialog__shift-group lineman-form-dialog__shift-group--inline">
              {shifts.map((item) => (
                <Box
                  key={item.shiftId}
                  component="label"
                  className={`lineman-form-dialog__shift-option lineman-form-dialog__shift-option--card ${
                    isShiftSelected(selectedShiftIds, item.shiftId)
                      ? "selected"
                      : ""
                  }`}
                >
                  <Checkbox
                    size="small"
                    checked={isShiftSelected(selectedShiftIds, item.shiftId)}
                    onChange={() => onShiftToggle(item.shiftId)}
                    sx={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                  />
                  <Box className="shift-icon" aria-hidden>
                    <ShiftIcon shiftName={item.shiftName} />
                  </Box>
                  <Box className="lineman-form-dialog__shift-text">
                    <Typography component="span" className="shift-name">
                      {item.shiftName}
                    </Typography>
                    <Typography component="span" className="shift-time">
                      ({item.startTime} - {item.endTime})
                    </Typography>
                  </Box>
                </Box>
              ))}
            </FormGroup>
            {showError("iv_shift") ? (
              <FormHelperText error sx={{ mx: 0 }}>
                {showError("iv_shift")}
              </FormHelperText>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Phone No</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_phone"
              value={formik.values.iv_phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: 11 }}
              error={Boolean(showError("iv_phone"))}
              helperText={showError("iv_phone")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>CNIC</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_cnic"
              value={formik.values.iv_cnic}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: 13 }}
              error={Boolean(showError("iv_cnic"))}
              helperText={showError("iv_cnic")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Employee Code</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_employeeCode"
              value={formik.values.iv_employeeCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(showError("iv_employeeCode"))}
              helperText={showError("iv_employeeCode")}
              className="lineman-form-dialog__input"
              sx={editableInputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>User Id.</Typography>
            <TextField
              fullWidth
              size="small"
              name="iv_userId"
              value={formik.values.iv_userId}
              disabled
              sx={readOnlyInputSx}
              className="lineman-form-dialog__designation"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className="lineman-form-dialog__actions">
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          className="lineman-form-dialog__btn-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={formik.handleSubmit}
          className="lineman-form-dialog__btn-submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditLinemanDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formik: PropTypes.object.isRequired,
  apiError: PropTypes.string,
  shifts: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedShiftIds: PropTypes.array.isRequired,
  onShiftToggle: PropTypes.func.isRequired,
  designations: PropTypes.arrayOf(PropTypes.object).isRequired,
  subdivisionDesignations: PropTypes.arrayOf(PropTypes.object),
  designationLabel: PropTypes.string,
};
